// src/pages/appointments/call/AppointmentVideo.jsx //
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import CallSurface from "@/components/appointments/call/CallSurface.jsx";
import CallControls from "@/components/appointments/call/CallControls.jsx";
import CallActionSheet from "@/components/appointments/call/CallActionSheet.jsx";
import { CALL_STATES } from "@/components/appointments/call/callStates.js";
import {
  buildConsultationSummaryState,
  CALL_MEDIA_STATES,
  resolveConsultationParticipants,
} from "@/components/appointments/call/consultationSession.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import useCallUiState from "@/components/appointments/call/useCallUiState.js";
import useAppointmentCallSnapshot from "@/pages/appointments/call/useAppointmentCallSnapshot.js";
import videoConsultationService from "@/services/videoConsultationService.js";
import { resolveValidatedSession } from "@/lib/authSession";
import {
  acquireSafeConsultationStream,
  getFriendlyMediaJoinMessage,
  isRequestedDeviceUnavailableError,
} from "@/services/consultationDeviceSafety.js";
import {
  CONSULTATION_AUDIO_CONSTRAINTS,
  attachTrackToContainer,
  bindParticipantTracks,
  clearMediaContainer,
  connectManagedConsultationRoom,
  detachTrack,
  getLocalTrack,
  releaseManagedConsultationRoomMedia,
} from "@/services/managedConsultationMedia.js";

const ICONS = {
  more: "/icons/appointments/video-call/more-filled.svg",
  micOn: "/icons/appointments/video-call/mic-on-24-filled.svg",
  micOff: "/icons/appointments/video-call/mic-off-24-filled.svg",
  videoFrame: "/icons/appointments/video-call/video-frame.svg",
  hangUp: "/icons/appointments/video-call/hang-up-1-solid.svg",
  sound: "/icons/appointments/video-call/sound-on-solid.svg",
  soundOff: "/icons/appointments/video-call/sound-off-solid.svg",
  shrink: "/icons/appointments/video-call/shrink-dark.svg",
  switchCam: "/icons/appointments/video-call/switch-camera-dark.svg",
  maximize: "/icons/appointments/video-call/maximize-dark.svg",
  profileMale: "/icons/appointments/video-call/profile_male.svg",
  doctorFallback: "/icons/appointments/video-call/video-doctor.jpg",
};

const AVATAR_PLACEHOLDER = "/icons/circular-avatar-placeholder.svg";

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function classifyMediaError(error) {
  const name = String(error?.name || "").trim();
  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  ) {
    return CALL_MEDIA_STATES.DENIED;
  }
  if (
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "OverconstrainedError"
  ) {
    return CALL_MEDIA_STATES.MISSING;
  }
  return CALL_MEDIA_STATES.UNAVAILABLE;
}

function mountPreviewStream(container, stream) {
  if (!container || !stream) return;
  clearMediaContainer(container);
  const element = document.createElement("video");
  element.autoplay = true;
  element.muted = true;
  element.playsInline = true;
  element.className = "h-full w-full object-cover";
  element.srcObject = stream;
  container.appendChild(element);
  element.play?.().catch(() => {});
}

function readTranscriptText(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  const candidates = [
    value.text,
    value.content,
    value.transcript,
    value.transcription,
    value.message,
    value.alternatives?.[0]?.transcript,
    value.results?.[0]?.alternatives?.[0]?.transcript,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === "object") {
      const nested = readTranscriptText(candidate);
      if (nested) return nested;
    }
  }
  return "";
}

function readTranscriptFinalFlag(value) {
  if (!value || typeof value !== "object") return true;
  const candidates = [
    value.isFinal,
    value.final,
    value.finalized,
    value.stable,
    value.status,
    value.transcription?.isFinal,
    value.transcription?.final,
    value.transcription?.status,
    value.result?.isFinal,
    value.results?.[0]?.isFinal,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "boolean") return candidate;
    if (typeof candidate === "string") {
      const lower = candidate.toLowerCase();
      if (["final", "finalized", "completed", "complete"].includes(lower)) return true;
      if (["partial", "interim", "in_progress"].includes(lower)) return false;
    }
  }
  return true;
}

function normalizeTranscriptSpeaker(value, participants) {
  const identity = String(
    value?.participant?.identity ||
      value?.speaker?.identity ||
      value?.participantIdentity ||
      value?.speakerIdentity ||
      value?.track?.participant?.identity ||
      value?.track?.sid ||
      ""
  ).trim();
  const lower = identity.toLowerCase();
  const localRole = String(participants?.actorRole || "").toLowerCase();
  const remoteRole = localRole === "doctor" ? "patient" : "doctor";

  if (lower.includes("doctor")) {
    return {
      speakerType: "doctor",
      speakerName:
        participants?.doctor?.displayName ||
        (localRole === "doctor" ? participants?.local?.displayName : participants?.remote?.displayName) ||
        "Doctor",
    };
  }

  if (lower.includes("patient")) {
    return {
      speakerType: "patient",
      speakerName:
        participants?.patient?.displayName ||
        (localRole === "patient" ? participants?.local?.displayName : participants?.remote?.displayName) ||
        "Patient",
    };
  }

  if (identity && value?.participant?.identity === identity) {
    return {
      speakerType: remoteRole === "patient" ? "patient" : "doctor",
      speakerName: participants?.remote?.displayName || remoteRole,
    };
  }

  return {
    speakerType: localRole === "patient" ? "patient" : "doctor",
    speakerName: participants?.local?.displayName || localRole || "Clinician",
  };
}

function normalizeTwilioTranscriptEvent(value, participants) {
  const content = readTranscriptText(value);
  if (!content) return null;
  const final = readTranscriptFinalFlag(value);
  const speaker = normalizeTranscriptSpeaker(value, participants);
  const providerEventId = String(
    value?.sid ||
      value?.transcriptionSid ||
      value?.eventSid ||
      value?.result?.sid ||
      `${speaker.speakerType}:${content}:${value?.timestamp || ""}`
  ).trim();

  return {
    ...speaker,
    content,
    isFinal: final,
    confidenceScore:
      Number(value?.confidence || value?.confidenceScore || value?.result?.confidence) ||
      null,
    timestampInSession: value?.timestamp || value?.time || null,
    providerEventId,
    provider: "twilio_video",
    rawEvent: value && typeof value === "object" ? value : { content },
  };
}

export default function AppointmentVideo() {
  const nav = useNavigate();
  const auth = useAuth() || {};
  const authLoading = auth?.loading === true;
  const authUserId = String(auth?.user?.id || "").trim();
  const { t } = useLanguage();
  const { id } = useParams();
  const location = useLocation();
  const routeState = location.state || null;
  const audioOnly = new URLSearchParams(location.search).get("media") === "audio" || routeState?.mediaKind === "audio";
  const [audioJoinAttempt, setAudioJoinAttempt] = React.useState(0);
  const [audioJoinBusy, setAudioJoinBusy] = React.useState(false);
  const audioJoinMountedRef = React.useRef(true);
  React.useEffect(() => { audioJoinMountedRef.current = true; return () => { audioJoinMountedRef.current = false; }; }, []);
  const [audioJoinClosed, setAudioJoinClosed] = React.useState(false);
  const [remoteParticipantPresent, setRemoteParticipantPresent] = React.useState(false);
  const liveAppointmentId = React.useMemo(
    () => (looksLikeUuid(id) ? String(id) : ""),
    [id]
  );
  const { snapshot: restored, loading, error } = useAppointmentCallSnapshot(
    id,
    routeState
  );
  const participants = React.useMemo(
    () =>
      resolveConsultationParticipants({ auth, routeState, snapshot: restored }),
    [auth, restored, routeState]
  );
  const doctorPostConsultationState = React.useMemo(
    () => ({
      ...(restored || {}),
      ...(routeState || {}),
      actorRole: "doctor",
      appointmentId: String(id || ""),
      patientId:
        routeState?.patientId ||
        restored?.patientId ||
        restored?.patient_id ||
        restored?.patient?.user_id ||
        restored?.patient?.id ||
        null,
      patient:
        routeState?.patient ||
        restored?.patient ||
        null,
      counterpartName: participants.remote.displayName,
      counterpartAvatar: participants.remote.avatar,
      endedAt: new Date().toISOString(),
    }),
    [id, participants.remote.avatar, participants.remote.displayName, restored, routeState]
  );
  const selfThumb = participants.local.avatar;
  const hasRealLocalAvatar =
    Boolean(String(selfThumb || "").trim()) &&
    String(selfThumb || "").trim() !== AVATAR_PLACEHOLDER;
  const hasRealRemoteAvatar =
    Boolean(String(participants.remote.avatar || "").trim()) &&
    String(participants.remote.avatar || "").trim() !== AVATAR_PLACEHOLDER;

  const localMediaRef = React.useRef(null);
  const remoteVideoRef = React.useRef(null);
  const remoteAudioRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const roomRef = React.useRef(null);
  const disconnectingRef = React.useRef(false);
  const roomCleanupRef = React.useRef(() => {});
  const participantCleanupsRef = React.useRef(new Map());
  const transcriptEventKeysRef = React.useRef(new Set());

  const [cameraOn, setCameraOn] = React.useState(!audioOnly);
  const [micControlOn, setMicControlOn] = React.useState(true);
  const [currentDeviceId, setCurrentDeviceId] = React.useState(null);
  const [currentFacingMode, setCurrentFacingMode] = React.useState("user");
  const [sessionError, setSessionError] = React.useState("");
  const [remoteVideoActive, setRemoteVideoActive] = React.useState(false);
  const stableAuthUserIdRef = React.useRef("");
  const isConsultationWindowClosedError = React.useCallback((value) => {
    const code = String(value?.code || value?.body?.error || "").trim().toLowerCase();
    return code === "consultation_window_expired" || code === "consultation_closed";
  }, []);
  const {
    rootRef,
    phase,
    speakerOn,
    micOn,
    menuOpen,
    setMenuOpen,
    setPhase,
    showToast,
    toggleSpeaker,
    toggleMic,
    toggleFullscreen,
    setPermissionState,
    setDeviceState,
    toastMessage,
  } = useCallUiState({
    loading,
    error: error || sessionError,
    initialPhase: CALL_STATES.IDLE,
    kind: audioOnly ? "audio" : "video",
  });
  const speakerOnRef = React.useRef(speakerOn);
  const micOnRef = React.useRef(micOn);
  const cameraOnRef = React.useRef(cameraOn);
  const currentFacingModeRef = React.useRef(currentFacingMode);
  const requiresPatientJoinGesture =
    !audioOnly && Boolean(liveAppointmentId) &&
    participants.actorRole === "patient" &&
    routeState?.mediaPermissionPrimed !== true;

  React.useEffect(() => {
    if (authUserId) {
      stableAuthUserIdRef.current = authUserId;
    }
  }, [authUserId]);

  React.useEffect(() => {
    speakerOnRef.current = speakerOn;
  }, [speakerOn]);

  React.useEffect(() => {
    micOnRef.current = micOn;
  }, [micOn]);

  React.useEffect(() => {
    cameraOnRef.current = cameraOn;
  }, [cameraOn]);

  React.useEffect(() => {
    currentFacingModeRef.current = currentFacingMode;
  }, [currentFacingMode]);

  const clearParticipantBindings = React.useCallback(() => {
    participantCleanupsRef.current.forEach((cleanup) => cleanup?.());
    participantCleanupsRef.current.clear();
    clearMediaContainer(remoteVideoRef.current);
    clearMediaContainer(remoteAudioRef.current);
    setRemoteVideoActive(false);
  }, []);

  const getPreviewStream = React.useCallback(async (mediaDevices, preferredInput = null) => {
    const requestedFacingMode =
      preferredInput && typeof preferredInput === "object"
        ? preferredInput.facingMode || null
        : null;
    const preferredDeviceId =
      preferredInput && typeof preferredInput === "object"
        ? preferredInput.deviceId || null
        : preferredInput;

    if (audioOnly) return mediaDevices.getUserMedia({ audio: CONSULTATION_AUDIO_CONSTRAINTS, video: false });

    const primaryConstraints = {
      audio: CONSULTATION_AUDIO_CONSTRAINTS,
      video: preferredDeviceId
        ? { deviceId: { exact: preferredDeviceId } }
        : {
            facingMode:
              requestedFacingMode || currentFacingModeRef.current || "user",
          },
    };

    try {
      return await mediaDevices.getUserMedia(primaryConstraints);
    } catch (error) {
      if (!isRequestedDeviceUnavailableError(error)) throw error;

      return mediaDevices.getUserMedia({
        audio: CONSULTATION_AUDIO_CONSTRAINTS,
        video: {
          facingMode: requestedFacingMode || currentFacingModeRef.current || "user",
        },
      });
    }
  }, [audioOnly]);

  const restartVideoTrackWithFallback = React.useCallback(async (track, primaryConstraints) => {
    await track.restart(primaryConstraints);
  }, []);

  const stopPreviewTracks = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    clearMediaContainer(localMediaRef.current);
  }, []);

  const releaseLocalConsultationMedia = React.useCallback((room = roomRef.current) => {
    releaseManagedConsultationRoomMedia(room);
    stopPreviewTracks();
    clearMediaContainer(localMediaRef.current);
  }, [stopPreviewTracks]);

  const syncMicControlState = React.useCallback(() => {
    const liveAudioTrack = getLocalTrack(roomRef.current, "audio");
    const liveEnabled = liveAudioTrack?.mediaStreamTrack?.enabled;
    if (typeof liveEnabled === "boolean") {
      setMicControlOn(liveEnabled);
      return liveEnabled;
    }

    const previewAudioTrack = streamRef.current?.getAudioTracks?.()[0];
    if (typeof previewAudioTrack?.enabled === "boolean") {
      setMicControlOn(previewAudioTrack.enabled);
      return previewAudioTrack.enabled;
    }

    setMicControlOn(micOnRef.current);
    return micOnRef.current;
  }, []);

  const isMobileCameraSurface = React.useCallback(() => {
    if (typeof window === "undefined") return false;
    const matchesViewport =
      window.matchMedia?.("(max-width: 767px)")?.matches ?? false;
    const agent = String(window.navigator?.userAgent || "");
    return matchesViewport || /iPhone|iPad|iPod|Android/i.test(agent);
  }, []);

  const applySpeakerState = React.useCallback((enabled) => {
    const container = remoteAudioRef.current;
    if (!container) return;
    container.querySelectorAll("audio").forEach((element) => {
      element.muted = !enabled;
      element.volume = 1;
    });
  }, []);

  const bindRemoteParticipant = React.useCallback(
    (participant) => {
      if (!participant?.sid) return;

      const cleanup = bindParticipantTracks(participant, {
        onTrackSubscribed: (track) => {
          if (track.kind === "video") {
            if (audioOnly) return;
            attachTrackToContainer(track, remoteVideoRef.current, {
              className: "h-full w-full object-cover",
            });
            setRemoteVideoActive(true);
            return;
          }

          if (track.kind === "audio") {
            attachTrackToContainer(track, remoteAudioRef.current, {
              muted: false,
              className: "hidden",
            });
            applySpeakerState(speakerOnRef.current);
          }
        },
        onTrackUnsubscribed: (track) => {
          detachTrack(track);
          if (track.kind === "video") {
            clearMediaContainer(remoteVideoRef.current);
            setRemoteVideoActive(false);
          }
        },
      });

      participantCleanupsRef.current.set(participant.sid, cleanup);
    },
    [applySpeakerState, audioOnly]
  );

  const syncLocalTracks = React.useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    const localAudioTrack = getLocalTrack(room, "audio");
    if (localAudioTrack) {
      if (micOnRef.current) localAudioTrack.enable?.();
      else localAudioTrack.disable?.();
      setMicControlOn(
        Boolean(localAudioTrack.mediaStreamTrack?.enabled ?? micOnRef.current)
      );
      setPermissionState("microphone", CALL_MEDIA_STATES.READY);
      setDeviceState("microphone", CALL_MEDIA_STATES.READY);
    } else {
      setMicControlOn(micOnRef.current);
      setDeviceState("microphone", CALL_MEDIA_STATES.MISSING);
    }

    const localVideoTrack = getLocalTrack(room, "video");
    if (localVideoTrack) {
      const nextDeviceId =
        localVideoTrack.mediaStreamTrack?.getSettings?.().deviceId || null;
      if (nextDeviceId) setCurrentDeviceId(nextDeviceId);
      const nextFacingMode =
        localVideoTrack.mediaStreamTrack?.getSettings?.().facingMode || null;
      if (nextFacingMode) setCurrentFacingMode(nextFacingMode);
      setPermissionState("camera", CALL_MEDIA_STATES.READY);
      setDeviceState("camera", CALL_MEDIA_STATES.READY);
      if (cameraOnRef.current) {
        localVideoTrack.enable?.();
        attachTrackToContainer(localVideoTrack, localMediaRef.current, {
          muted: true,
          className: "h-full w-full object-cover",
        });
      } else {
        localVideoTrack.disable?.();
        clearMediaContainer(localMediaRef.current);
      }
    } else {
      setDeviceState("camera", CALL_MEDIA_STATES.MISSING);
      clearMediaContainer(localMediaRef.current);
    }
  }, [
    setDeviceState,
    setPermissionState,
    setCurrentDeviceId,
    setCurrentFacingMode,
  ]);

  const disconnectLiveRoom = React.useCallback(
    async ({ finalize = false, reason = "left_consultation" } = {}) => {
      disconnectingRef.current = true;
      roomCleanupRef.current?.();
      roomCleanupRef.current = () => {};
      clearParticipantBindings();

      const room = roomRef.current;
      roomRef.current = null;
      releaseLocalConsultationMedia(room);
      if (room && room.state !== "disconnected") {
        room.disconnect();
      }

      if (liveAppointmentId) {
        await videoConsultationService.markAppointmentSessionEnded(
          liveAppointmentId,
          {
            reason,
            finalize,
          }
        );
      }
    },
    [clearParticipantBindings, liveAppointmentId, releaseLocalConsultationMedia]
  );

  const startFallbackPreview = React.useCallback(
    async (preferredInput = null, options = {}) => {
      const settlePhase = options?.settlePhase || CALL_STATES.IN_CALL;
      const nextPhase = streamRef.current
        ? CALL_STATES.RECONNECTING
        : CALL_STATES.CONNECTING;
      setPhase(nextPhase);

      try {
        const mediaDevices = navigator.mediaDevices;
        if (!mediaDevices?.getUserMedia) {
          setDeviceState("camera", CALL_MEDIA_STATES.UNAVAILABLE);
          setDeviceState("microphone", CALL_MEDIA_STATES.UNAVAILABLE);
          setPhase(CALL_STATES.IN_CALL);
          setCameraOn(false);
          return;
        }

        stopPreviewTracks();

        const requestedFacingMode =
          preferredInput && typeof preferredInput === "object"
            ? preferredInput.facingMode || null
            : null;

        const stream = await getPreviewStream(mediaDevices, preferredInput);

        streamRef.current = stream;

        const audioTrack = stream.getAudioTracks()[0];
        const videoTrack = stream.getVideoTracks()[0];
        setPermissionState("microphone", CALL_MEDIA_STATES.READY);
        setDeviceState(
          "microphone",
          audioTrack ? CALL_MEDIA_STATES.READY : CALL_MEDIA_STATES.MISSING
        );
        setPermissionState("camera", CALL_MEDIA_STATES.READY);
        setDeviceState(
          "camera",
          videoTrack ? CALL_MEDIA_STATES.READY : CALL_MEDIA_STATES.MISSING
        );
        if (audioTrack) audioTrack.enabled = micOnRef.current;
        setMicControlOn(Boolean(audioTrack?.enabled ?? micOnRef.current));
        if (videoTrack) {
          videoTrack.enabled = cameraOnRef.current;
          const nextDeviceId = videoTrack.getSettings?.().deviceId || null;
          if (nextDeviceId) setCurrentDeviceId(nextDeviceId);
          const nextFacingMode =
            videoTrack.getSettings?.().facingMode || requestedFacingMode || null;
          if (nextFacingMode) setCurrentFacingMode(nextFacingMode);
        }

        if (cameraOnRef.current) {
          mountPreviewStream(localMediaRef.current, stream);
        } else {
          clearMediaContainer(localMediaRef.current);
        }
        setPhase(settlePhase);
      } catch (previewError) {
        const mediaState = classifyMediaError(previewError);
        setCameraOn(false);
        if (mediaState === CALL_MEDIA_STATES.DENIED) {
          setPermissionState("camera", CALL_MEDIA_STATES.DENIED);
          setPermissionState("microphone", CALL_MEDIA_STATES.DENIED);
        } else {
          setDeviceState("camera", mediaState);
        }
        setPhase(settlePhase);
      }
    },
    [
      getPreviewStream,
      setCurrentDeviceId,
      setCurrentFacingMode,
      setDeviceState,
      setPermissionState,
      setPhase,
      stopPreviewTracks,
    ]
  );

  React.useEffect(() => {
    if (!liveAppointmentId || loading || authLoading || (audioOnly && !audioJoinAttempt)) return undefined;
    if (requiresPatientJoinGesture) {
      nav(`/appointments/details/${liveAppointmentId}`, {
        replace: true,
        state: {
          ...(routeState || {}),
          appointmentId: liveAppointmentId,
          videoReady: true,
        },
      });
      return undefined;
    }

    let cancelled = false;
    let pendingJoinTracks = [];

    async function connectLiveRoom() {
      disconnectingRef.current = false;
      setSessionError("");
      setPhase(CALL_STATES.CONNECTING);

      try {
        const validatedSession = await resolveValidatedSession({
          signOutOnFailure: false,
        });
        const effectiveUserId = String(
          authUserId ||
            validatedSession?.user?.id ||
            stableAuthUserIdRef.current ||
            ""
        ).trim();
        if (!effectiveUserId) {
          throw new Error("Session expired. Please sign in again.");
        }

        const { data: joinData, error: joinError } =
          await videoConsultationService.getAppointmentJoinCredentials(
            liveAppointmentId,
            { preferFreshSession: true }
          );

        if (joinError) throw joinError;
        if (!joinData?.credentials?.accessToken || !joinData?.room?.name) {
          throw new Error(
            t(
              "appointments.call.transport.joinUnavailable",
              "The consultation could not start right now."
            )
          );
        }

        let joinTracks = [];
        if (streamRef.current) {
          joinTracks = streamRef.current
            .getTracks()
            .filter((track) => track && track.readyState !== "ended");
          joinTracks.forEach((track) => {
            if (track.kind === "audio") track.enabled = micOnRef.current;
            if (track.kind === "video") track.enabled = cameraOnRef.current;
          });
          clearMediaContainer(localMediaRef.current);
        } else {
          const safeStream = await acquireSafeConsultationStream({
            appointmentId: liveAppointmentId,
            route: window.location?.pathname || "",
            preferVideo: !audioOnly,
          });
          streamRef.current = safeStream;
          joinTracks = safeStream
            .getTracks()
            .filter((track) => track && track.readyState !== "ended");
          joinTracks.forEach((track) => {
            if (track.kind === "audio") track.enabled = micOnRef.current;
            if (track.kind === "video") track.enabled = cameraOnRef.current;
          });
        }
        if (audioOnly) {
          joinTracks.filter((track) => track.kind === "video").forEach((track) => track.stop());
          joinTracks = joinTracks.filter((track) => track.kind === "audio");
          if (!joinTracks.length) throw new Error("No microphone found. Connect a microphone and retry.");
        }
        pendingJoinTracks = joinTracks;

        const room = await connectManagedConsultationRoom({
          accessToken: joinData.credentials.accessToken,
          roomName: joinData.room.name,
          kind: audioOnly ? "audio" : "video",
          tracks: joinTracks,
        });

        if (cancelled) {
          releaseManagedConsultationRoomMedia(room);
          room.disconnect();
          return;
        }

        pendingJoinTracks = [];
        roomRef.current = room;
        streamRef.current = null;
        syncLocalTracks();
        room.participants.forEach((participant) => bindRemoteParticipant(participant));
        applySpeakerState(speakerOnRef.current);
        if (audioOnly) showToast("");
        setPhase(CALL_STATES.IN_CALL);

        const handleParticipantConnected = (participant) => {
          bindRemoteParticipant(participant);
          showToast(
            t(
              "appointments.call.transport.participantJoined",
              "Participant joined the consultation."
            )
          );
        };

        const handleParticipantDisconnected = (participant) => {
          participantCleanupsRef.current.get(participant?.sid)?.();
          participantCleanupsRef.current.delete(participant?.sid);
          clearMediaContainer(remoteVideoRef.current);
          clearMediaContainer(remoteAudioRef.current);
          setRemoteVideoActive(false);
          showToast(
            t(
              "appointments.call.transport.participantLeft",
              "Participant left the consultation."
            )
          );
        };

        const handleReconnecting = () => {
          setPhase(CALL_STATES.RECONNECTING);
        };

        const handleReconnected = () => {
          setSessionError("");
          syncLocalTracks();
          syncMicControlState();
          setPhase(CALL_STATES.IN_CALL);
        };

        const handleDisconnected = async () => {
          roomCleanupRef.current?.();
          roomCleanupRef.current = () => {};
          clearParticipantBindings();
          roomRef.current = null;
          releaseLocalConsultationMedia(room);

          if (!disconnectingRef.current && liveAppointmentId) {
            await videoConsultationService.markAppointmentSessionEnded(
              liveAppointmentId,
              {
                reason: "connection_lost",
                finalize: false,
              }
            );
            showToast(
              t(
                "appointments.call.transport.disconnected",
                "Consultation connection ended."
              )
            );
            setSessionError(audioOnly ? "Connection lost. You can rejoin the audio call while the appointment is open." : "Consultation connection ended.");
            setPhase(CALL_STATES.ENDED);
          }
        };

        const handleTranscription = async (event) => {
          const normalized = normalizeTwilioTranscriptEvent(event, participants);
          if (!normalized?.isFinal || !normalized.content || !liveAppointmentId) return;

          const dedupeKey =
            normalized.providerEventId ||
            `${normalized.speakerType}:${normalized.content}`.slice(0, 240);
          if (transcriptEventKeysRef.current.has(dedupeKey)) return;
          transcriptEventKeysRef.current.add(dedupeKey);

          const { error: transcriptError } =
            await videoConsultationService.persistAppointmentTranscriptLine(
              liveAppointmentId,
              normalized
            );
          if (transcriptError) {
            console.warn(
              "[AppointmentVideo] transcript persistence failed:",
              transcriptError?.message || transcriptError
            );
          }
        };

        room.on("participantConnected", handleParticipantConnected);
        room.on("participantDisconnected", handleParticipantDisconnected);
        room.on("reconnecting", handleReconnecting);
        room.on("reconnected", handleReconnected);
        room.on("disconnected", handleDisconnected);
        room.on?.("transcription", handleTranscription);

        roomCleanupRef.current = () => {
          room.off("participantConnected", handleParticipantConnected);
          room.off("participantDisconnected", handleParticipantDisconnected);
          room.off("reconnecting", handleReconnecting);
          room.off("reconnected", handleReconnected);
          room.off("disconnected", handleDisconnected);
          room.off?.("transcription", handleTranscription);
        };

        try {
          await videoConsultationService.markAppointmentSessionStarted(
            liveAppointmentId,
            {
              connectionState: "connected",
              deviceState: {
                camera: audioOnly ? "off" : CALL_MEDIA_STATES.READY,
                microphone: CALL_MEDIA_STATES.READY,
              },
            }
          );
        } catch (sessionStartError) {
          // The consultation is already live at this point; do not replace
          // an active call UI with a stale auth error from background bookkeeping.
          console.warn(
            "[AppointmentVideo] markAppointmentSessionStarted failed:",
            sessionStartError?.message || sessionStartError
          );
        }
      } catch (connectError) {
        if (cancelled) return;
        pendingJoinTracks.forEach((track) => {
          try {
            track.stop?.();
          } catch {
            // noop
          }
        });
        pendingJoinTracks = [];

        if (isConsultationWindowClosedError(connectError)) {
          if (audioOnly) setAudioJoinClosed(true);
          const message =
            connectError?.message ||
            t(
              "appointments.call.transport.joinUnavailable",
              "The consultation could not start right now."
            );
          setSessionError(message);
          setPhase(CALL_STATES.ENDED);
          showToast(message);
          stopPreviewTracks();
          return;
        }

        const mediaState = classifyMediaError(connectError);
        if (mediaState === CALL_MEDIA_STATES.DENIED) {
          if (!audioOnly) setPermissionState("camera", CALL_MEDIA_STATES.DENIED);
          setPermissionState("microphone", CALL_MEDIA_STATES.DENIED);
        } else if (mediaState === CALL_MEDIA_STATES.MISSING) {
          setDeviceState(audioOnly ? "microphone" : "camera", CALL_MEDIA_STATES.MISSING);
        }

        const message =
          (audioOnly && /permission|access.*blocked/i.test(connectError?.message || "")
            ? "Microphone access is blocked. Allow microphone access in your browser settings, then rejoin."
            : getFriendlyMediaJoinMessage(connectError)) ||
          t(
            "appointments.call.transport.joinUnavailable",
            "The consultation could not start right now."
          );
        setSessionError(message);
        showToast(message);
        stopPreviewTracks();
        setPhase(CALL_STATES.ENDED);
      }
    }

    connectLiveRoom();

    return () => {
      cancelled = true;
      pendingJoinTracks.forEach((track) => {
        try {
          track.stop?.();
        } catch {
          // noop
        }
      });
      pendingJoinTracks = [];
      roomCleanupRef.current?.();
      roomCleanupRef.current = () => {};
      clearParticipantBindings();
      const room = roomRef.current;
      roomRef.current = null;
      releaseLocalConsultationMedia(room);
      if (room && room.state !== "disconnected") {
        disconnectingRef.current = true;
        room.disconnect();
      }
    };
  }, [
    audioOnly,
    audioJoinAttempt,
    applySpeakerState,
    authLoading,
    authUserId,
    bindRemoteParticipant,
    clearParticipantBindings,
    isConsultationWindowClosedError,
    liveAppointmentId,
    loading,
    nav,
    requiresPatientJoinGesture,
    releaseLocalConsultationMedia,
    routeState,
    setDeviceState,
    setPermissionState,
    setPhase,
    showToast,
    startFallbackPreview,
    stopPreviewTracks,
    syncLocalTracks,
    t,
  ]);

  React.useEffect(() => {
    if (audioOnly || liveAppointmentId || loading) return undefined;

    startFallbackPreview();
    return () => {
      stopPreviewTracks();
    };
  }, [audioOnly, liveAppointmentId, loading, startFallbackPreview, stopPreviewTracks]);

  React.useEffect(() => {
    applySpeakerState(speakerOn);
  }, [applySpeakerState, speakerOn]);

  React.useEffect(() => {
    const localAudioTrack = getLocalTrack(roomRef.current, "audio");
    if (localAudioTrack) {
      if (micOn) localAudioTrack.enable?.();
      else localAudioTrack.disable?.();
      syncMicControlState();
      return;
    }

    const previewAudioTrack = streamRef.current?.getAudioTracks?.()[0];
    if (previewAudioTrack) {
      previewAudioTrack.enabled = micOn;
      syncMicControlState();
      return;
    }
    setMicControlOn(micOn);
  }, [micOn, syncMicControlState]);

  async function hangUp() {
    const hadConnectedRoom = Boolean(
      roomRef.current && roomRef.current.state !== "disconnected"
    );
    stopPreviewTracks();
    if (hadConnectedRoom) {
      await disconnectLiveRoom({
        finalize: true,
        reason: "ended_by_participant",
      });
    }
    setPhase(CALL_STATES.ENDED);
    if (hadConnectedRoom) {
      if (participants.actorRole === "doctor") {
        nav(`/doctors/appointments/${id}/diagnosis`, {
          replace: true,
          state: doctorPostConsultationState,
        });
        return;
      }

      nav(`/appointments/summary/${id}`, {
        replace: true,
        state: buildConsultationSummaryState({
          appointmentId: id,
          routeState,
          snapshot: restored,
          participants,
        }),
      });
      return;
    }

    nav(routeState?.returnTo || `/appointments/details/${id}`, {
      replace: true,
      state: routeState || restored || null,
    });
  }

  async function toggleCamera() {
    const localVideoTrack = getLocalTrack(roomRef.current, "video");
    if (localVideoTrack) {
      const next = !cameraOn;
      setCameraOn(next);
      if (next) {
        localVideoTrack.enable?.();
        attachTrackToContainer(localVideoTrack, localMediaRef.current, {
          muted: true,
          className: "h-full w-full object-cover",
        });
        setDeviceState("camera", CALL_MEDIA_STATES.READY);
        showToast(
          t("appointments.video.toast.cameraReady", "Camera ready.")
        );
      } else {
        localVideoTrack.disable?.();
        clearMediaContainer(localMediaRef.current);
        showToast(
          t("appointments.video.toast.cameraOff", "Camera off.")
        );
      }
      return;
    }

    const previewTrack = streamRef.current?.getVideoTracks?.()[0];
    if (previewTrack) {
      const next = !cameraOn;
      previewTrack.enabled = next;
      setCameraOn(next);
      if (next) mountPreviewStream(localMediaRef.current, streamRef.current);
      else clearMediaContainer(localMediaRef.current);
      setDeviceState("camera", CALL_MEDIA_STATES.READY);
      showToast(
        next
          ? t("appointments.video.toast.cameraReady", "Camera ready.")
          : t("appointments.video.toast.cameraOff", "Camera off.")
      );
      return;
    }

    setCameraOn(true);
    await startFallbackPreview(currentDeviceId);
  }

  async function switchCamera() {
    try {
      const nextFacingMode = currentFacingMode === "environment" ? "user" : "environment";
      const mediaDevices = navigator.mediaDevices;
      if (!mediaDevices?.enumerateDevices) {
        setDeviceState("camera", CALL_MEDIA_STATES.UNAVAILABLE);
        showToast(
          t(
            "appointments.video.toast.switchCameraUnavailable",
            "Switch camera is not available on this device."
          )
        );
        return;
      }

      const localVideoTrack = getLocalTrack(roomRef.current, "video");
      if (isMobileCameraSurface() && localVideoTrack?.restart) {
        await restartVideoTrackWithFallback(localVideoTrack, {
          facingMode: nextFacingMode,
          width: 1280,
        });
        setCurrentFacingMode(nextFacingMode);
        const restartedDeviceId =
          localVideoTrack.mediaStreamTrack?.getSettings?.().deviceId || null;
        if (restartedDeviceId) setCurrentDeviceId(restartedDeviceId);
        attachTrackToContainer(localVideoTrack, localMediaRef.current, {
          muted: true,
          className: "h-full w-full object-cover",
        });
        setDeviceState("camera", CALL_MEDIA_STATES.READY);
        showToast(
          t("appointments.video.toast.cameraSwitched", "Camera switched.")
        );
        return;
      }

      if (isMobileCameraSurface() && streamRef.current) {
        setCurrentFacingMode(nextFacingMode);
        await startFallbackPreview({ facingMode: nextFacingMode });
        showToast(
          t("appointments.video.toast.cameraSwitched", "Camera switched.")
        );
        return;
      }

      const cameras = (await mediaDevices.enumerateDevices()).filter(
        (device) => device.kind === "videoinput"
      );
      if (cameras.length < 2) {
        setDeviceState("camera", CALL_MEDIA_STATES.READY);
        showToast(
          t("appointments.video.toast.singleCamera", "Only one camera is available.")
        );
        return;
      }

      const currentIndex = cameras.findIndex(
        (camera) => camera.deviceId === currentDeviceId
      );
      const nextCamera = cameras[(currentIndex + 1 + cameras.length) % cameras.length];

      if (localVideoTrack?.restart) {
        try {
          await restartVideoTrackWithFallback(localVideoTrack, {
            deviceId: { exact: nextCamera.deviceId },
            width: 1280,
          });
        } catch (error) {
          if (!isRequestedDeviceUnavailableError(error)) throw error;
          await restartVideoTrackWithFallback(localVideoTrack, {
            width: 1280,
          });
        }
        setCurrentDeviceId(nextCamera.deviceId);
        attachTrackToContainer(localVideoTrack, localMediaRef.current, {
          muted: true,
          className: "h-full w-full object-cover",
        });
        setDeviceState("camera", CALL_MEDIA_STATES.READY);
        showToast(
          t("appointments.video.toast.cameraSwitched", "Camera switched.")
        );
        return;
      }

      setCurrentDeviceId(nextCamera.deviceId);
      await startFallbackPreview(nextCamera.deviceId);
      showToast(
        t("appointments.video.toast.cameraSwitched", "Camera switched.")
      );
    } catch {
      setDeviceState("camera", CALL_MEDIA_STATES.UNAVAILABLE);
      showToast(
        t("appointments.video.toast.cameraSwitchFailed", "Unable to switch camera.")
      );
    }
  }

  async function requestAudioJoin() {
    if (audioJoinBusy || !authUserId || audioJoinClosed || !liveAppointmentId) return;
    setAudioJoinBusy(true);
    setSessionError("");
    try {
      // Permission is requested from the actual Join gesture, before creating
      // a provider room/token. Audio must never request a camera as a fallback.
      const stream = await acquireSafeConsultationStream({
        appointmentId: liveAppointmentId, route: window.location.pathname, preferVideo: false,
      });
      if (!audioJoinMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      if (!stream.getAudioTracks().length) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("No microphone found. Connect a microphone and retry.");
      }
      stopPreviewTracks();
      stream.getAudioTracks().forEach((track) => { track.enabled = micOnRef.current; });
      streamRef.current = stream;
      setAudioJoinAttempt((value) => value + 1);
    } catch (error) {
      if (!audioJoinMountedRef.current) return;
      stopPreviewTracks();
      const message = /permission|access.*blocked/i.test(error?.message || "")
        ? "Microphone access is blocked. Allow microphone access in your browser settings, then rejoin."
        : getFriendlyMediaJoinMessage(error);
      setSessionError(message);
      setPhase(CALL_STATES.ENDED);
    } finally { if (audioJoinMountedRef.current) setAudioJoinBusy(false); }
  }

  return (
    <>
      {audioOnly ? <div className="fixed left-1/2 top-20 z-[60] w-[min(92vw,480px)] -translate-x-1/2 rounded-2xl bg-white p-4 text-center text-slate-900 shadow-lg" aria-live="polite">
        <p className="font-semibold">{participants.remote.displayName} · Internet audio call</p>
        <p>Camera off</p>
        {sessionError ? <p role="alert" className="mt-2">{sessionError}</p> : <p>{phase === CALL_STATES.IN_CALL ? "Connected. Your camera stays off." : phase === CALL_STATES.CONNECTING ? "Connecting microphone…" : phase === CALL_STATES.RECONNECTING ? "Connection interrupted. Reconnecting…" : "Use your microphone to join this appointment."}</p>}
        {phase === CALL_STATES.IN_CALL ? <p>{remoteParticipantPresent ? "Both participants are in the call." : "Waiting for the other participant."}</p> : null}
        {!liveAppointmentId ? <p role="alert">A valid appointment is required for an audio call.</p> : null}
        {audioJoinClosed ? <button type="button" onClick={() => nav(routeState?.returnTo || `/appointments/details/${id}`)}>Back to appointment</button> : null}
        {!audioJoinClosed && (!audioJoinAttempt || phase === CALL_STATES.ENDED) ? <button type="button" disabled={audioJoinBusy || !authUserId || !liveAppointmentId || loading || authLoading || !!error} className="mt-3 rounded-xl bg-blue-700 px-5 py-3 text-white disabled:opacity-50" onClick={requestAudioJoin}>{audioJoinBusy ? "Allow microphone…" : audioJoinAttempt ? "Rejoin audio call" : "Join audio call"}</button> : null}
        {error ? <p role="alert">{error}</p> : null}
      </div> : null}
      <CallSurface
        rootRef={rootRef}
        title={participants.remote.displayName}
        icons={{
          shrink: ICONS.shrink,
          maximize: ICONS.maximize,
          switchCam: ICONS.switchCam,
          profileMale: ICONS.profileMale,
        }}
        labels={{
          shrink: t("appointments.call.controls.shrink", "Shrink"),
          switchCamera: t(
            "appointments.call.controls.switchCamera",
            "Switch camera"
          ),
          maximize: t("appointments.call.controls.maximize", "Maximize"),
        }}
        headerTone="light"
        titleTone="light"
        onShrink={() =>
          showToast(
            t(
              "appointments.call.toast.consultationMinimized",
              "Consultation view minimized."
            )
          )
        }
        onSwitchCamera={audioOnly ? undefined : switchCamera}
        onMaximize={toggleFullscreen}
        otherThumb={participants.remote.avatar}
        thumbSlot={
          cameraOn ? (
            <div
              ref={localMediaRef}
              className="h-full w-full overflow-hidden"
            />
          ) : hasRealLocalAvatar ? (
            <img
              src={selfThumb}
              alt=""
              className="h-full w-full object-cover"
              draggable="false"
            />
          ) : null
        }
        toast={toastMessage}
        childrenBackground={
          <>
            <div
              ref={remoteVideoRef}
              className={`absolute inset-0 transition-opacity duration-200 ${
                remoteVideoActive ? "opacity-100" : "opacity-0"
              }`}
            />
            {!remoteVideoActive ? (
              hasRealRemoteAvatar ? (
                <img
                  src={participants.remote.avatar || ICONS.doctorFallback}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  draggable="false"
                />
              ) : (
                <>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(52,211,214,0.08),_transparent_26%),linear-gradient(180deg,#0A0D13_0%,#0E1320_100%)]" />
                  <img
                    src={ICONS.profileMale}
                    alt=""
                    draggable="false"
                    className="absolute left-1/2 bottom-[-88px] w-[100vw] max-w-none -translate-x-1/2 md:bottom-[-168px] md:w-[1220px]"
                    style={{
                      opacity: 0.42,
                      filter:
                        "brightness(0) saturate(100%) invert(75%) sepia(9%) saturate(412%) hue-rotate(183deg) brightness(87%) contrast(88%)",
                    }}
                  />
                </>
              )
            ) : null}
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,8,23,0.20),rgba(2,8,23,0.12)_40%,rgba(2,8,23,0.24))]" />
            <div ref={remoteAudioRef} className="hidden" />
          </>
        }
        childrenControls={
          <CallControls
            items={[
              {
                key: "more",
                label: t("appointments.call.controls.more", "More"),
                icon: ICONS.more,
                onClick: () => setMenuOpen(true),
              },
              !audioOnly && {
                key: "video",
                label: t("appointments.video.controls.camera", "Video"),
                icon: ICONS.videoFrame,
                onClick: toggleCamera,
                tone: "primary",
                active: cameraOn,
                disabled: loading || !!error,
              },
              {
                key: "speaker",
                label: t("appointments.call.controls.speaker", "Speaker"),
                icon: speakerOn ? ICONS.sound : ICONS.soundOff,
                onClick: () => toggleSpeaker(),
                active: speakerOn,
              },
              {
                key: "mic",
                label: t("appointments.call.controls.mic", "Mic"),
                icon: micControlOn ? ICONS.micOn : ICONS.micOff,
                onClick: () => toggleMic(),
                active: micControlOn,
              },
              {
                key: "hangup",
                label: t("appointments.call.controls.end", "End"),
                icon: ICONS.hangUp,
                onClick: hangUp,
                tone: "danger",
              },
            ]}
          />
        }
      />

      <CallActionSheet
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={t("appointments.video.sheet.title", "Consultation options")}
        actions={[
          !audioOnly && {
            key: "camera",
            label: t("appointments.video.sheet.cameraPreview", "Camera preview"),
            description: cameraOn
              ? t(
                  "appointments.video.sheet.cameraPreviewOn",
                  "Turn your camera preview off."
                )
              : t(
                  "appointments.video.sheet.cameraPreviewOff",
                  "Turn your camera preview on."
                ),
            onClick: toggleCamera,
          },
          !audioOnly && {
            key: "switch",
            label: t("appointments.call.controls.switchCamera", "Switch camera"),
            description: t(
              "appointments.video.sheet.switchCameraDescription",
              "Swap between available front and rear cameras."
            ),
            onClick: switchCamera,
          },
          {
            key: "fullscreen",
            label: t("appointments.call.sheet.fullscreen", "Fullscreen"),
            description: t(
              "appointments.call.sheet.fullscreenDescription",
              "Expand this consultation surface on desktop."
            ),
            onClick: toggleFullscreen,
          },
        ]}
      />
    </>
  );
}
